/**
 * Code Navigation Utilities
 *
 * Search algorithms for finding HTML elements in source code
 * based on click events in the preview iframe.
 */

/**
 * Find the line number of an element in HTML source code
 *
 * Uses a multi-tier search strategy:
 * 1. Search by ID (highest priority)
 * 2. Search by class name + text verification
 * 3. Search by unique text content
 * 4. Search by tag pattern
 *
 * @param {string} htmlContent - The full HTML source code
 * @param {Object} elementData - Data about the clicked element
 * @param {string} elementData.id - Element ID
 * @param {string} elementData.className - Element class names
 * @param {string} elementData.tagName - Element tag name (lowercase)
 * @param {string} elementData.textContent - Element text content (first 50 chars)
 * @returns {number} - Line number (1-indexed) or -1 if not found
 */
export function findElementInCode(htmlContent, elementData) {
  const lines = htmlContent.split('\n');
  let targetLine = -1;

  // Strategy 1: Search by ID
  if (elementData.id) {
    targetLine = searchById(lines, elementData.id);
    if (targetLine !== -1) {
      return targetLine + 1; // Convert to 1-indexed
    }
  }

  // Strategy 2: Search by class + text verification
  if (elementData.className) {
    targetLine = searchByClass(lines, elementData.className, elementData.textContent);
    if (targetLine !== -1) {
      return targetLine + 1;
    }
  }

  // Strategy 3: Search by unique text content
  if (elementData.textContent) {
    targetLine = searchByText(lines, elementData.textContent);
    if (targetLine !== -1) {
      return targetLine + 1;
    }
  }

  // Strategy 4: Search by tag pattern
  if (elementData.tagName) {
    targetLine = searchByTag(lines, elementData.tagName);
    if (targetLine !== -1) {
      return targetLine + 1;
    }
  }

  return -1; // Not found
}

/**
 * Search for element by ID attribute
 */
function searchById(lines, id) {
  // Escape special regex characters in ID
  const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const idPattern1 = new RegExp(`id=["']${escapedId}["']`);
  const idPattern2 = new RegExp(`id=["']\\s*${escapedId}\\s*["']`);

  for (let i = 0; i < lines.length; i++) {
    if (idPattern1.test(lines[i]) || idPattern2.test(lines[i])) {
      return i;
    }
  }

  return -1;
}

/**
 * Search for element by class name
 * If multiple matches, verify with nearby text content
 */
function searchByClass(lines, className, textContent) {
  // Extract first class name (ignore multiple classes for now)
  const firstClass = className.trim().split(/\s+/)[0];
  if (!firstClass) return -1;

  const escapedClass = firstClass.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const classPattern = new RegExp(`class=["'][^"']*\\b${escapedClass}\\b[^"']*["']`);

  const candidates = [];
  for (let i = 0; i < lines.length; i++) {
    if (classPattern.test(lines[i])) {
      candidates.push(i);
    }
  }

  // If only one match, return it
  if (candidates.length === 1) {
    return candidates[0];
  }

  // If multiple matches and we have text content, find best match
  if (candidates.length > 1 && textContent) {
    for (const lineIndex of candidates) {
      // Check 3 lines before and after for text match
      const contextStart = Math.max(0, lineIndex - 3);
      const contextEnd = Math.min(lines.length, lineIndex + 4);
      const context = lines.slice(contextStart, contextEnd).join(' ');

      if (context.includes(textContent)) {
        return lineIndex;
      }
    }
  }

  // If we have candidates but couldn't verify, return first one
  if (candidates.length > 0) {
    return candidates[0];
  }

  return -1;
}

/**
 * Search for element by unique text content
 */
function searchByText(lines, textContent) {
  // Note: escapedText could be used for regex matching, but currently using simple includes()
  // const _escapedText = textContent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const matches = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(textContent)) {
      matches.push(i);
    }
  }

  // Only return if text is unique
  if (matches.length === 1) {
    return matches[0];
  }

  return -1;
}

/**
 * Search for opening tag pattern
 */
function searchByTag(lines, tagName) {
  const tagPattern = new RegExp(`<${tagName}[\\s>]`, 'i');

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = lines[i].trim();
    if (tagPattern.test(trimmedLine)) {
      return i;
    }
  }

  return -1;
}

/**
 * Extract meaningful element data from a DOM element
 * Used by the iframe click handler
 *
 * @param {HTMLElement} element - The clicked DOM element
 * @returns {Object} Element data for code navigation
 */
export function extractElementData(element) {
  return {
    id: element.id || '',
    className: element.className || '',
    tagName: element.tagName.toLowerCase(),
    textContent: element.textContent?.trim().slice(0, 50) || '',
  };
}

/**
 * Walk up the DOM tree to find a meaningful element
 * Skips inline elements like <span>, <a>, <strong>, etc.
 *
 * @param {HTMLElement} element - Starting element
 * @returns {HTMLElement|null} Meaningful parent element or null
 */
export function findMeaningfulElement(element) {
  const meaningfulTags = [
    'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'NAV',
    'TABLE', 'UL', 'OL', 'LI', 'FORM', 'BLOCKQUOTE',
    'PRE', 'CODE', 'FIGURE', 'MAIN', 'ASIDE'
  ];

  let current = element;
  let depth = 0;
  const maxDepth = 10; // Prevent infinite loops

  while (current && depth < maxDepth) {
    if (meaningfulTags.includes(current.tagName)) {
      return current;
    }
    current = current.parentElement;
    depth++;
  }

  // Fallback to original element if no meaningful parent found
  return element;
}
